'use client';

import Image from "next/image";
import Link from "next/link";
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import LanguageSwitcher from '../LanguageSwitcher';
import { useSession } from '@/stores/session';
import { useAuth } from '@/lib/hooks/useAuth';
import { User } from '@/lib/types/api';

interface HeaderContentProps {
  locale: string;
  user?: User | null; // Optional - artık kullanılmıyor, sadece geriye dönük uyumluluk için
  isAuthenticated?: boolean; // Optional - artık kullanılmıyor, sadece geriye dönük uyumluluk için
  translations: {
    home: string;
    auctions: string;
    requestForm: string;
    login: string;
    register: string;
    profile: string;
    dashboard: string;
    logout: string;
  };
}

export default function HeaderContent({ 
  locale, 
  translations 
}: HeaderContentProps) {
  const pathname = usePathname();
  const { user, isLoading } = useSession(); // Sadece client-side session'dan user bilgisini al
  const { logout } = useAuth();
  
  // Hydration için güvenli state
  const [mounted, setMounted] = useState(false);
  const [currentPathname, setCurrentPathname] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCurrentPathname(pathname);
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Sadece client-side session'dan gelen user'ı kullan
  const isAuthenticated = !!user;
  
  // Aktif sayfayı tespit et
  const isActive = (path: string) => {
    if (!mounted) return false;
    
    if (path === `/${locale}`) {
      return currentPathname === `/${locale}` || currentPathname === `/${locale}/`;
    }
    return currentPathname.startsWith(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200">
      <div className="relative w-full h-14 sm:h-16">
        {/* Sol taraf - Logo */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 pl-4 sm:pl-6 lg:pl-12">
          <Link href={`/${locale}`} className="block" prefetch={false}>
            <Image
              src="/images/logos/TRINK-LOGO.svg"
              alt="Trink Emlak - Gayrimenkul Açık Artırma Platformu"
              width={150}
              height={64}
              sizes="(max-width: 640px) 72px, (max-width: 1024px) 104px, 128px"
              className="block h-8 w-auto sm:h-10 lg:h-12 hover:opacity-80 transition-opacity"
              priority
              fetchPriority="high"
              unoptimized
            />
          </Link>
        </div>
        
        {/* Orta - Navigasyon menüsü (Desktop) */}
        <nav className="hidden lg:flex items-center justify-center h-full space-x-8">
          <Link 
            href={`/${locale}`}
            prefetch={false}
            className={`relative font-medium transition-colors duration-200 ${
              isActive(`/${locale}`) 
                ? 'text-red-600' 
                : 'text-gray-700 hover:text-red-600'
            }`}
          >
            {translations.home}
            {isActive(`/${locale}`) && (
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-red-600"></div>
            )}
          </Link>
          <Link 
            href={`/${locale}/muzayedeler`}
            prefetch={false}
            className={`relative font-medium transition-colors duration-200 ${
              isActive(`/${locale}/muzayedeler`) 
                ? 'text-red-600' 
                : 'text-gray-700 hover:text-red-600'
            }`}
          >
            {translations.auctions}
            {isActive(`/${locale}/muzayedeler`) && (
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-red-600"></div>
            )}
          </Link>
          <Link 
            href={`/${locale}/talep-formu`}
            prefetch={false}
            className={`relative font-medium transition-colors duration-200 ${
              isActive(`/${locale}/talep-formu`) 
                ? 'text-red-600' 
                : 'text-gray-700 hover:text-red-600'
            }`}
          >
            {translations.requestForm}
            {isActive(`/${locale}/talep-formu`) && (
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-red-600"></div>
            )}
          </Link>
        </nav>
        
        {/* Sağ taraf - Butonlar */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-4 sm:pr-6 lg:pr-12 flex items-center space-x-2 sm:space-x-3">
          {/* Desktop - Giriş/Kayıt butonları */}
          {!isLoading && (
          <div className="hidden lg:flex items-center space-x-3">
            {isAuthenticated && user ? (
              // Logged in user
              <div className="flex items-center space-x-3">
                <div className="hidden xl:block text-sm text-gray-700">
                  {user.firstName} {user.lastName}
                </div>
                {mounted && user.roles?.includes('Admin') && (
                  <Link 
                    href="/yonetim-paneli"
                    prefetch={false}
                    className="p-2 rounded-full transition-colors text-gray-600 hover:text-red-600 hover:bg-red-50"
                    title={translations.dashboard}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </Link>
                )}
                {mounted && !user.roles?.includes('Admin') && !isActive(`/${locale}/hesabim`) && (
                  <Link 
                    href={`/${locale}/hesabim`}
                    prefetch={false}
                    className="p-2 rounded-full transition-colors text-gray-600 hover:text-red-600 hover:bg-red-50"
                    title={translations.profile}
                  >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Link>
                )}
                <button
                  onClick={logout}
                  className="p-2 rounded-full transition-colors text-gray-600 hover:text-red-600 hover:bg-red-50"
                  title={translations.logout}
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            ) : (
              // Guest user
              <>
                <Link 
                  href={`/${locale}/giris`}
                  prefetch={false}
                  className="px-3 py-2 rounded-full font-medium transition-colors text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
                >
                  {translations.login}
                </Link>
                <Link 
                  href={`/${locale}/kayit`}
                  prefetch={false}
                  className="px-3 py-2 rounded-full font-medium transition-colors text-sm text-white bg-red-600 hover:bg-red-700 border border-red-600"
                >
                  {translations.register}
                </Link>
              </>
            )}
          </div>
          )}

          {/* Dil değiştirici */}
          <LanguageSwitcher />

          {/* Mobile - Menü butonu */}
          <button
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-red-600 hover:bg-gray-100 transition-colors"
            aria-label={isMobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navbar */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white px-4 py-4 shadow-md">
          <nav className="flex flex-col gap-2">
            <Link
              href={`/${locale}`}
              prefetch={false}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(`/${locale}`) ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {translations.home}
            </Link>
            <Link
              href={`/${locale}/muzayedeler`}
              prefetch={false}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(`/${locale}/muzayedeler`) ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {translations.auctions}
            </Link>
            <Link
              href={`/${locale}/talep-formu`}
              prefetch={false}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(`/${locale}/talep-formu`) ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {translations.requestForm}
            </Link>

            {!isLoading && (
              <div className="mt-2 pt-3 border-t border-gray-100 flex flex-col gap-2">
                {isAuthenticated && user ? (
                  <>
                    <div className="px-3 text-sm text-gray-700">
                      {user.firstName} {user.lastName}
                    </div>
                    {mounted && user.roles?.includes('Admin') && (
                      <Link
                        href="/yonetim-paneli"
                        prefetch={false}
                        className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {translations.dashboard}
                      </Link>
                    )}
                    {mounted && !user.roles?.includes('Admin') && (
                      <Link
                        href={`/${locale}/hesabim`}
                        prefetch={false}
                        className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {translations.profile}
                      </Link>
                    )}
                    <button
                      onClick={logout}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-left text-red-600 hover:bg-red-50"
                    >
                      {translations.logout}
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link
                      href={`/${locale}/giris`}
                      prefetch={false}
                      className="flex-1 px-3 py-2 rounded-lg text-center font-medium text-sm text-gray-700 bg-gray-100 border border-gray-200"
                    >
                      {translations.login}
                    </Link>
                    <Link
                      href={`/${locale}/kayit`}
                      prefetch={false}
                      className="flex-1 px-3 py-2 rounded-lg text-center font-medium text-sm text-white bg-red-600 border border-red-600"
                    >
                      {translations.register}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      )}

    </header>
  );
}
